/**
 * PATH: apps/qa/src/pages/ChatPage.tsx
 * PURPOSE: Team collaboration chat for QA inspections
 * 
 * WHY: Site teams need to communicate about:
 * - Inspection progress and issues
 * - Hold point coordination
 * - NCR discussions
 * - Evidence clarifications
 * 
 * FLOW:
 *   ┌─────────────┐    ┌──────────────┐    ┌─────────────┐
 *   │ Select      │───▶│ Load Chat    │───▶│ Send/Recv   │
 *   │ Inspection  │    │ Messages     │    │ Messages    │
 *   └─────────────┘    └──────────────┘    └─────────────┘
 * 
 * DEPENDENCIES:
 *   - @quotech/api for API client
 *   - @tanstack/react-query for data fetching
 */

import { useState, useRef, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuthStore } from '@quotech/auth'
import api, { inspectionApi } from '@/lib/api'
import { cn } from '@/lib/utils'
import type { Inspection } from '@/types/qa'
import {
  MessageSquare,
  Send,
  Search,
  Image,
  Paperclip,
  AtSign,
  Hash,
  Loader2,
  MoreVertical,
  Smile,
  Pin,
} from 'lucide-react'

// ============================================================================
// Types (aligned with backend ChatMessageSerializer)
// ============================================================================

interface ChatMessage {
  id: string
  inspection: string
  area: string | null
  user: string
  user_name: string
  message_text: string
  asset_ids: string[]
  created_at: string
}

interface ChatResponse {
  results: ChatMessage[]
  count: number
}

// ============================================================================
// API (uses /qa/messages/ endpoint)
// ============================================================================

async function fetchMessages(inspectionId: string): Promise<ChatResponse> {
  const response = await api().get('/qa/messages/', {
    params: { inspection_id: inspectionId }
  })
  return response.data
}

async function sendMessage(inspectionId: string, messageText: string): Promise<ChatMessage> {
  const response = await api().post('/qa/messages/', {
    inspection: inspectionId,
    message_text: messageText,
  })
  return response.data
}

// ============================================================================
// Component
// ============================================================================

export default function ChatPage() {
  const [selectedInspection, setSelectedInspection] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [message, setMessage] = useState('')
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const queryClient = useQueryClient()

  // Wait for org context before fetching
  const organisationId = useAuthStore((s) => s.organisationId)
  const isAuthReady = !!organisationId

  // Fetch inspections for sidebar
  const { data: inspectionsData, isLoading: loadingInspections } = useQuery({
    queryKey: ['qa-inspections-for-chat', organisationId],
    queryFn: () => inspectionApi.getInspections({ page_size: 100 }),
    enabled: isAuthReady,
  })
  
  const showLoadingInspections = loadingInspections || !isAuthReady

  // Fetch messages for selected inspection
  const { data: messagesData, isLoading: loadingMessages } = useQuery({
    queryKey: ['qa-chat-messages', selectedInspection],
    queryFn: () => fetchMessages(selectedInspection!),
    enabled: !!selectedInspection,
    refetchInterval: 10000, // Poll every 10 seconds
  })

  // Send message mutation
  const sendMutation = useMutation({
    mutationFn: ({ content }: { content: string }) =>
      sendMessage(selectedInspection!, content),
    onSuccess: () => {
      setMessage('')
      setReplyingTo(null)
      queryClient.invalidateQueries({ queryKey: ['qa-chat-messages', selectedInspection] })
    },
  })

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messagesData?.results])

  // Filter inspections by search — safe guard against non-array responses
  const inspectionsList = Array.isArray(inspectionsData?.results) ? inspectionsData.results : []
  const filteredInspections = inspectionsList.filter((inspection: Inspection) => {
    if (!searchQuery) return true
    return inspection.inspection_name?.toLowerCase().includes(searchQuery.toLowerCase())
  })

  const handleSendMessage = () => {
    if (!message.trim() || !selectedInspection) return
    sendMutation.mutate({ content: message })
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSendMessage()
    }
  }

  return (
    <div className="flex h-full overflow-hidden">
      {/* Sidebar - Inspection List */}
      <div className="w-80 border-r border-border bg-card flex flex-col flex-shrink-0">
        {/* Header */}
        <div className="p-4 border-b border-border">
          <h2 className="text-lg font-bold text-foreground flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-primary" />
            Team Chat
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Collaborate on inspections
          </p>
        </div>

        {/* Search */}
        <div className="p-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search inspections..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-3 py-2 bg-background border border-input rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
        </div>

        {/* Inspection List */}
        <div className="flex-1 overflow-y-auto">
          {showLoadingInspections ? (
            <div className="p-4 space-y-3">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="h-16 bg-muted rounded-lg animate-pulse" />
              ))}
            </div>
          ) : filteredInspections?.length ? (
            <div className="p-2 space-y-1">
              {filteredInspections.map((inspection: Inspection) => (
                <button
                  key={inspection.id}
                  onClick={() => setSelectedInspection(inspection.id)}
                  className={cn(
                    'w-full p-3 rounded-lg text-left transition-all',
                    selectedInspection === inspection.id
                      ? 'bg-primary/10 border border-primary/30'
                      : 'hover:bg-accent'
                  )}
                >
                  <div className="flex items-center gap-3">
                    <div className={cn(
                      'w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0',
                      selectedInspection === inspection.id
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted text-muted-foreground'
                    )}>
                      <Hash className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm text-foreground truncate">
                        {inspection.inspection_name || 'Untitled'}
                      </p>
                      <p className="text-xs text-muted-foreground truncate">
                        {inspection.category || 'No category'}
                      </p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <div className="p-4 text-center text-sm text-muted-foreground">
              No inspections found
            </div>
          )}
        </div>
      </div>

      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col bg-slate-50 dark:bg-slate-900/50 min-w-0">
        {selectedInspection ? (
          <>
            {/* Chat Header */}
            <div className="p-4 bg-card border-b border-border flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-primary/10 text-primary">
                  <Hash className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground">
                    {inspectionsData?.results?.find((i: Inspection) => i.id === selectedInspection)?.inspection_name || 'Chat'}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {messagesData?.count || 0} messages
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button className="p-2 rounded-lg hover:bg-accent transition-colors text-muted-foreground hover:text-foreground">
                  <Pin className="w-4 h-4" />
                </button>
                <button className="p-2 rounded-lg hover:bg-accent transition-colors text-muted-foreground hover:text-foreground">
                  <MoreVertical className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {loadingMessages ? (
                <div className="flex items-center justify-center h-full">
                  <Loader2 className="w-6 h-6 animate-spin text-primary" />
                </div>
              ) : messagesData?.results?.length ? (
                <>
                  {messagesData.results.map((msg) => (
                    <MessageBubble
                      key={msg.id}
                      message={msg}
                    />
                  ))}
                  <div ref={messagesEndRef} />
                </>
              ) : (
                <div className="flex flex-col items-center justify-center h-full text-center">
                  <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                    <MessageSquare className="w-8 h-8 text-primary" />
                  </div>
                  <h3 className="font-semibold text-foreground">No messages yet</h3>
                  <p className="text-sm text-muted-foreground mt-1 max-w-sm">
                    Start a conversation about this inspection. Mention team members with @
                  </p>
                </div>
              )}
            </div>

            {/* Message Input */}
            <div className="p-4 bg-card border-t border-border">
              <div className="flex items-end gap-2">
                <div className="flex-1 bg-background rounded-xl border border-input focus-within:ring-2 focus-within:ring-ring overflow-hidden">
                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Type a message... (Enter to send, Shift+Enter for new line)"
                    rows={1}
                    className="w-full px-4 py-3 text-foreground placeholder:text-muted-foreground focus:outline-none resize-none bg-transparent"
                    style={{ minHeight: '48px', maxHeight: '120px' }}
                  />
                  <div className="px-4 pb-3 flex items-center gap-2">
                    <button className="p-1.5 rounded-lg hover:bg-accent text-muted-foreground hover:text-foreground transition-colors">
                      <AtSign className="w-4 h-4" />
                    </button>
                    <button className="p-1.5 rounded-lg hover:bg-accent text-muted-foreground hover:text-foreground transition-colors">
                      <Image className="w-4 h-4" />
                    </button>
                    <button className="p-1.5 rounded-lg hover:bg-accent text-muted-foreground hover:text-foreground transition-colors">
                      <Paperclip className="w-4 h-4" />
                    </button>
                    <button className="p-1.5 rounded-lg hover:bg-accent text-muted-foreground hover:text-foreground transition-colors">
                      <Smile className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <button
                  onClick={handleSendMessage}
                  disabled={!message.trim() || sendMutation.isPending}
                  className={cn(
                    'p-3 rounded-xl transition-all',
                    message.trim()
                      ? 'bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg shadow-primary/25'
                      : 'bg-muted text-muted-foreground cursor-not-allowed'
                  )}
                >
                  {sendMutation.isPending ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <Send className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>
          </>
        ) : (
          /* No Inspection Selected */
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center max-w-md">
              <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center mx-auto mb-6">
                <MessageSquare className="w-10 h-10 text-primary" />
              </div>
              <h2 className="text-xl font-bold text-foreground">Select an Inspection</h2>
              <p className="text-muted-foreground mt-2">
                Choose an inspection from the sidebar to start chatting with your team
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// ============================================================================
// Message Bubble Component
// ============================================================================

function MessageBubble({
  message,
}: {
  message: ChatMessage
}) {
  const isCurrentUser = false // TODO: Check against current user ID

  return (
    <div
      className={cn('flex gap-3 group', isCurrentUser && 'flex-row-reverse')}
    >
      {/* Avatar */}
      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-primary/70 flex items-center justify-center flex-shrink-0 text-white font-medium">
        {message.user_name?.charAt(0).toUpperCase() || 'U'}
      </div>

      {/* Message Content */}
      <div className={cn('flex-1 max-w-xl', isCurrentUser && 'text-right')}>
        <div className="flex items-center gap-2 mb-1">
          <span className="font-medium text-sm text-foreground">{message.user_name}</span>
          <span className="text-xs text-muted-foreground">
            {new Date(message.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </span>
        </div>

        <div className={cn(
          'relative inline-block rounded-2xl px-4 py-2',
          isCurrentUser
            ? 'bg-primary text-primary-foreground rounded-tr-none'
            : 'bg-card border border-border rounded-tl-none'
        )}>
          <p className="text-sm whitespace-pre-wrap">{message.message_text}</p>
        </div>
      </div>
    </div>
  )
}
